import React, { useState, useEffect } from "react";
import {
  Sun,
  Moon,
  Activity,
  Package,
  LogOut,
  ShieldCheck,
  Cookie,
  LogIn,
  ChevronDown,
  UserCircle,
  Settings,
  AlertTriangle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { openCookieSettings } from "../lib/consent";
import Notification from "./Notification";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import clinicalSettingsService from "../services/clinicalSettingsService";
import GlobalOmnisearch from "./search/GlobalOmnisearch";

interface HeaderProps {
  isDark: boolean;
  setIsDark: (dark: boolean) => void;
  useMockData: boolean;
  setUseMockData: (mock: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onShowLogin: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isDark,
  setIsDark,
  useMockData,
  setUseMockData,
  onShowLogin,
}) => {
  useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: string;
  } | null>(null);

  const [hospitalName, setHospitalName] = useState(() => {
    const prefsName = clinicalSettingsService.getHospitalPreferences().hospitalName;
    if (user?.hospital_name && user.hospital_name.trim() !== "Hospital" && user.hospital_name.trim() !== "") {
      return user.hospital_name;
    }
    return prefsName || "DaAFYA Medical Center";
  });

  useEffect(() => {
    const handleSettingsUpdate = (e: any) => {
      if (e.detail?.hospitalName) {
        setHospitalName(e.detail.hospitalName);
      }
    };
    window.addEventListener("hms_hospital_preferences_updated", handleSettingsUpdate);
    if (user?.hospital_name && user.hospital_name.trim() !== "Hospital" && user.hospital_name.trim() !== "") {
      setHospitalName(user.hospital_name);
    }
    return () =>
      window.removeEventListener("hms_hospital_preferences_updated", handleSettingsUpdate);
  }, [user]);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const toggleAPI = () => {
    if (!isAuthenticated() && !useMockData) {
      setNotification({
        message: "Please sign in to access real API data",
        type: "warning",
      });
      return;
    }
    setUseMockData(!useMockData);
  };

  useEffect(() => {
    if (isAuthenticated() && useMockData) {
      setNotification({
        message: "Login successful! You can now toggle to real API data.",
        type: "success",
      });
    }
  }, [isAuthenticated(), useMockData]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const handleLogin = () => {
    onShowLogin?.();
    setShowUserMenu(false);
  };

  return (
    <header
      className={`sticky top-0 z-20 backdrop-blur border-b select-none transition-colors ${
        isDark
          ? "bg-neutral-950/70 border-neutral-800"
          : "bg-white/80 border-gray-200"
      }`}
    >
      <div className="w-full px-4 h-12 flex items-center justify-between gap-3">
        {/* Left: Hospital Name & Status Badge */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="text-xs sm:text-sm font-semibold text-foreground truncate max-w-[200px] lg:max-w-[300px]"
            title={hospitalName}
          >
            {hospitalName}
          </span>
          <span
            className={`hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              useMockData
                ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
            }`}
            title={useMockData ? "Using mock data" : "Connected to live API"}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                useMockData ? "bg-amber-500" : "bg-emerald-500 animate-pulse"
              }`}
            />
            <span>{useMockData ? "Mock" : "Live API"}</span>
          </span>
        </div>
        {/* Center: Top-Level Multi-Entity Global Omnisearch */}
        <div className="flex-1 max-w-md mx-2 hidden md:block">
          <GlobalOmnisearch isDark={isDark} />
        </div>

        {/* Right: Icon-only Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Emergency Code Trigger (Square Button) */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('openEmergencyCodeModal'))}
            className="h-8 w-8 rounded-lg border border-red-500/30 flex items-center justify-center text-red-500 hover:text-red-600 hover:bg-red-500/10 transition-colors"
            title="Activate Emergency Code"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
          </button>

          {/* API Toggle (Icon) */}
          <button
            type="button"
            onClick={toggleAPI}
            disabled={!isAuthenticated()}
            className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-colors relative ${
              !isAuthenticated()
                ? "border-border/40 text-muted-foreground/40 cursor-not-allowed"
                : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/70"
            }`}
            title={
              !isAuthenticated()
                ? "Login required for real API access"
                : `Switch to ${useMockData ? "real API" : "mock data"}`
            }
          >
            {useMockData ? (
              <Package className="w-3.5 h-3.5 text-amber-500" />
            ) : (
              <Activity className="w-3.5 h-3.5 text-emerald-500" />
            )}
          </button>

          {/* Theme Toggle (Icon) */}
          <button
            type="button"
            onClick={toggleTheme}
            className="h-8 w-8 rounded-lg border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
            title={`Switch to ${isDark ? "light" : "dark"} theme`}
          >
            {isDark ? (
              <Sun className="w-3.5 h-3.5 text-amber-400" />
            ) : (
              <Moon className="w-3.5 h-3.5 text-indigo-500" />
            )}
          </button>

          {/* Language Selector (Lean) */}
          <LanguageSelector isDark={isDark} />

          {/* User Profile / Sign In */}
          <div className="relative">
            {isAuthenticated() ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="h-8 px-2 rounded-lg border border-border/60 flex items-center gap-1.5 text-xs text-foreground hover:bg-muted/70 transition-colors"
                  title="Account"
                >
                  <div className="w-5 h-5 rounded-full bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400 flex items-center justify-center font-bold text-[10px]">
                    {(user?.username || "U")[0].toUpperCase()}
                  </div>
                  <span className="hidden sm:inline font-medium truncate max-w-[80px]">
                    {user?.username || "User"}
                  </span>
                  <ChevronDown className="w-3 h-3 text-muted-foreground" />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div
                    className={`absolute right-0 top-full mt-1.5 w-48 rounded-xl border shadow-lg z-50 ${
                      isDark
                        ? "border-neutral-800 bg-neutral-900"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="p-1.5">
                      <div className="px-2.5 py-1.5 text-xs border-b border-border/50 mb-1">
                        <div className="font-semibold text-foreground truncate">
                          {user?.username || "User"}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {hospitalName}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          navigate("/profile");
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <UserCircle className="w-3.5 h-3.5" />
                        <span>Profile</span>
                      </button>

                      <button
                        onClick={() => {
                          navigate("/settings");
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span>Settings</span>
                      </button>

                      <button
                        onClick={() => {
                          navigate("/privacy-policy");
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        <span>Privacy Policy</span>
                      </button>

                      <button
                        onClick={() => {
                          openCookieSettings();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Cookie className="w-3.5 h-3.5" />
                        <span>Cookie settings</span>
                      </button>


                      <div className="border-t border-border/50 my-1" />

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={handleLogin}
                className="h-8 px-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium flex items-center gap-1.5 transition-colors shadow-xs"
                title="Sign In"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type as any}
          onClose={() => setNotification(null)}
        />
      )}
    </header>
  );
};

export default Header;
